import { animate, useMotionValue, useReducedMotion, type AnimationPlaybackControls } from "motion/react";
import { useLayoutEffect, useRef } from "react";

// The clip edge is the only thing that moves, so the curve carries the whole transition: it leaves
// slowly, crosses the middle fast, and decelerates hard into the target, overshooting by a hair
// (y2 above 1) so the edge settles rather than stops. Short enough to read as the region opening in
// one gesture instead of as something being animated.
const HEIGHT_TRANSITION = {
  duration: 0.25,
  ease: [0.27, 0.01, 0, 1.01],
} as const;

/**
 * Eases a wrapper's height to whatever its content measures.
 *
 * The height lives in a MotionValue, so running it never re-renders React. That matters beyond
 * cost: `GiftCardSheet` puts `layout` on the panel and Motion re-measures it on every render, so a
 * height React could see change would make the panel FLIP — springing the screen to its new size
 * and scaling its text on the way — on top of this. Driven this way the panel measures no delta,
 * stays auto-height and simply follows this wrapper in normal flow, leaving its `layout` to do what
 * it was written for: morphing between screens.
 *
 * Nothing translates, which is the point of pinning the content to this wrapper's bottom edge. The
 * sheet is anchored to its own bottom too, so that edge is the one fixed line on the screen: the
 * content sits against it at its full height from the first frame, and the height animates the
 * clip window open upward over it. Anchored to the top instead, the content would hang below the
 * window and ride up into place — the region would read as sliding rather than opening.
 *
 * Reduced motion keeps the height explicit here and drops only the curve — going back to `auto`
 * there would hand the resize to the panel's FLIP and animate more, not less.
 *
 * A ResizeObserver rather than a key naming the variants: nothing has to be kept in sync with a
 * branch table, and `overflow: hidden` cannot clip content that grew for a reason the key forgot —
 * a wrapping error message, a longer label, a field added later.
 */
export const useAnimatedHeight = () => {
  const contentRef = useRef<HTMLDivElement>(null);
  const height = useMotionValue(0);
  const settledHeight = useRef(0);
  const controls = useRef<AnimationPlaybackControls | null>(null);

  // Read when a resize lands rather than captured by the effect, which stays mount-only: as a
  // dependency, a preference that flipped mid-run would tear the observer down and leave the
  // height stranded, since the target it was easing to is already what `settledHeight` holds.
  const prefersReducedMotion = useReducedMotion();
  const reduceMotion = useRef(prefersReducedMotion);
  reduceMotion.current = prefersReducedMotion;

  useLayoutEffect(() => {
    const node = contentRef.current;
    if (!node) return;

    const applyHeight = (next: number, animated: boolean) => {
      if (next === settledHeight.current) return;
      // Interrupting mid-run: `animate` picks the MotionValue up at its current height and carries
      // its velocity into the new target, so a second change lands on the first without a hitch.
      controls.current?.stop();
      settledHeight.current = next;

      if (!animated) {
        height.set(next);
        return;
      }
      controls.current = animate(height, next, HEIGHT_TRANSITION);
    };

    // `offsetHeight`, not `getBoundingClientRect()`: the sheet panel above is mid-FLIP-scale while
    // a screen swaps in, and a transformed rect would settle this region at a scaled height.
    // Before the first paint, so the region opens at its real height instead of easing up from 0.
    applyHeight(node.offsetHeight, false);

    const observer = new ResizeObserver(() =>
      applyHeight(node.offsetHeight, !reduceMotion.current),
    );
    observer.observe(node);

    return () => {
      observer.disconnect();
      controls.current?.stop();
      controls.current = null;
    };
  }, [height]);

  return { contentRef, height };
};
